import { ApplicantGroupType, RegistrationType } from '@exam-center/graphql';
import { type Path } from 'react-hook-form';
import { z, ZodIssueCode } from 'zod';

import { ExamDay, EventEndType } from './type';

const optionalNumber = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z.number().nullish(),
);

const MIN_EXAM_DURATION_MINUTES = 15;

export const BaseRecurringExamEventCreationFormSchema = z.object({
  examEventName: z.string().trim().nullable(),
  registrationType: z.nativeEnum(RegistrationType).nullish(),
  examCourses: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .optional(),
  examDateGroup: z
    .object({
      date: z.array(z.nativeEnum(ExamDay)).nullish(),
      examRecurringStartDate: z.date().nullish(),
      examRecurringEndDate: z.date().nullish(),
      startTime: z.date().nullish(),
      endTime: z.date().nullish(),
    })
    .optional(),
  location: z.object({
    province: z
      .object({
        label: z.string(),
        value: z.string().uuid(),
      })
      .nullish(),
    locationName: z
      .object({
        label: z.string(),
        value: z.string().uuid(),
      })
      .nullish(),
    room: z.record(z.string().uuid(), z.boolean().nullish()),
    seat: z.union([z.number(), z.literal('')]).nullish(),
  }),
  mainProctors: z
    .array(
      z.object({
        value: z.string().uuid(),
        label: z.string(),
      }),
    )
    .nullish(),
  eventStartDate: z.date().nullish(),
  eventEndDate: z
    .object({
      type: z.nativeEnum(EventEndType).nullish(),
      date: optionalNumber,
    })
    .optional(),
  groupType: z
    .object({
      label: z.string(),
      value: z.nativeEnum(ApplicantGroupType),
    })
    .nullish(),
  groupCode: z.string().nullish(),
});

export type RecurringExamEventCreationFormType = z.infer<
  typeof BaseRecurringExamEventCreationFormSchema
>;

let superRefineCtx: z.RefinementCtx | null = null;

type SessionKeys = 'startedAt' | 'endedAt';

type RecurringExamEventCreationFormTypeKeys =
  | Path<RecurringExamEventCreationFormType>
  | number
  | SessionKeys
  | 'root';

function addValidationIssue(args: {
  path: RecurringExamEventCreationFormTypeKeys[];
  message: string;
}) {
  if (!superRefineCtx) {
    console.error('BaseRecurringExamEventCreationFormSchema: superRefineCtx is not defined');
    return;
  }

  const { path, message } = args;
  superRefineCtx.addIssue({
    code: ZodIssueCode.custom,
    path,
    message,
  });
}
const allowCharactersRegex = /^[ก-๏a-zA-Z0-9\s]*$/;

const EXAM_EVENT_NAME_MIN_LENGTH = 1;
const EXAM_EVENT_NAME_MAX_LENGTH = 1024;

function validateAllowCharacters(input: string, path: RecurringExamEventCreationFormTypeKeys[]) {
  if (!allowCharactersRegex.test(input)) {
    addValidationIssue({
      path,
      message: 'อนุญาตเฉพาะ ก-ฮ, a-z, A-Z, 0-9 เท่านั้น',
    });
  }
}

function validateExamEventName(data: RecurringExamEventCreationFormType) {
  const { examEventName } = data;

  if (!examEventName) {
    addValidationIssue({
      path: ['examEventName'],
      message: 'กรุณากรอกชื่อรอบสอบ',
    });
    return;
  }

  const examEventNameLength = examEventName.trim().length;

  if (examEventNameLength < EXAM_EVENT_NAME_MIN_LENGTH) {
    addValidationIssue({
      path: ['examEventName'],
      message: 'กรุณากรอกชื่อรอบสอบ',
    });
  } else if (examEventNameLength > EXAM_EVENT_NAME_MAX_LENGTH) {
    addValidationIssue({
      path: ['examEventName'],
      message: `ความยาวต้องไม่เกิน ${EXAM_EVENT_NAME_MAX_LENGTH} ตัวอักษร`,
    });
  }

  validateAllowCharacters(examEventName, ['examEventName']);
}

function validateRegistrationType(data: RecurringExamEventCreationFormType) {
  const { registrationType } = data;

  if (!registrationType) {
    addValidationIssue({
      path: ['registrationType'],
      message: 'กรุณาเลือก Registration Type',
    });
  }
}

const EXAM_EVENT_SEAT_MIN_LENGTH = 1;

function validateLocation(data: RecurringExamEventCreationFormType) {
  const { location } = data;

  if (!location.province?.value) {
    addValidationIssue({
      path: ['location.province'],
      message: 'กรุณาเลือกจังหวัด',
    });
  }

  if (!location.locationName?.value) {
    addValidationIssue({
      path: ['location.locationName'],
      message: 'กรุณาเลือกสถานที่สอบ/หน่วยงาน',
    });
  }

  if (!location.room) {
    addValidationIssue({
      path: ['location.room'],
      message: 'กรุณาเลือกห้องสอบ',
    });
  }

  if (!location.seat || (typeof location.seat === 'string' && location.seat === '')) {
    addValidationIssue({
      path: ['location.seat'],
      message: 'กรุณากรอกจำนวนที่นั่ง',
    });
  }

  if (location.room) {
    const selectedRooms = Object.values(location.room).filter((room) => room);
    if (selectedRooms.length === 0) {
      addValidationIssue({
        path: ['location.room'],
        message: 'กรุณาเลือกห้องสอบ',
      });
    }
  }

  if (location.seat && location.seat < EXAM_EVENT_SEAT_MIN_LENGTH) {
    addValidationIssue({
      path: ['location.seat'],
      message: `จำนวนที่นั่งต้องมากกว่า ${EXAM_EVENT_SEAT_MIN_LENGTH - 1}`,
    });
  }
}

function validateExamDate(data: RecurringExamEventCreationFormType) {
  const { examDateGroup } = data;

  if (!examDateGroup?.date?.length) {
    addValidationIssue({
      path: ['examDateGroup.date'],
      message: 'กรุณาเลือกวันในสัปดาห์อย่างน้อย 1 วัน',
    });
  }

  if (!examDateGroup?.examRecurringStartDate) {
    addValidationIssue({
      path: ['examDateGroup.examRecurringStartDate'],
      message: 'ระบุวันที่เริ่มช่วงสอบ',
    });
  }

  if (!examDateGroup?.examRecurringEndDate) {
    addValidationIssue({
      path: ['examDateGroup.examRecurringEndDate'],
      message: 'ระบุวันที่สิ้นสุดช่วงสอบ',
    });
  }
  if (examDateGroup?.examRecurringStartDate && examDateGroup?.examRecurringEndDate) {
    if (examDateGroup.examRecurringStartDate > examDateGroup.examRecurringEndDate) {
      addValidationIssue({
        path: ['examDateGroup.examRecurringEndDate'],
        message: 'วันที่สิ้นสุดต้องมากกว่าวันที่เริ่มช่วงสอบ',
      });
    }
  }

  if (!examDateGroup?.startTime) {
    addValidationIssue({
      path: ['examDateGroup.startTime'],
      message: 'ระบุเวลาเริ่มต้น',
    });
  }

  if (!examDateGroup?.endTime) {
    addValidationIssue({
      path: ['examDateGroup.endTime'],
      message: 'ระบุเวลาสิ้นสุด',
    });
  }

  if (examDateGroup?.startTime && examDateGroup?.endTime) {
    if (examDateGroup.startTime >= examDateGroup.endTime) {
      addValidationIssue({
        path: ['examDateGroup.endTime'],
        message: 'เวลาสิ้นสุดต้องมากกว่าเวลาเริ่มต้น',
      });
    }
    const timeDifference =
      (examDateGroup.endTime.getTime() - examDateGroup.startTime.getTime()) / (1000 * 60);
    if (timeDifference < MIN_EXAM_DURATION_MINUTES) {
      addValidationIssue({
        path: ['examDateGroup.endTime'],
        message: `เวลาสอบต้องมีความยาวอย่างน้อย ${MIN_EXAM_DURATION_MINUTES} นาที`,
      });
    }
  }
}

function validateExamCourses(data: RecurringExamEventCreationFormType) {
  const { examCourses } = data;

  if (!examCourses || examCourses.length === 0) {
    addValidationIssue({
      path: ['examCourses'],
      message: 'กรุณาเลือกหลักสูตรที่เปิดสอบ',
    });
  }
}

const MINIMUM_EXAM_EVENT_CLOSE_BEFORE_EXAM_DATE = 1;

function validateEventDates(data: RecurringExamEventCreationFormType) {
  const { eventStartDate, eventEndDate, examDateGroup } = data;
  if (!eventStartDate) {
    addValidationIssue({
      path: ['eventStartDate'],
      message: 'กรุณาเลือกวันที่เปิดรับสมัคร',
    });
  }

  if (eventStartDate && examDateGroup?.examRecurringStartDate) {
    if (eventStartDate > examDateGroup.examRecurringStartDate) {
      addValidationIssue({
        path: ['eventStartDate'],
        message: 'วันเปิดรับสมัครต้องไม่มากกว่าวันเริ่มช่วงสอบ',
      });
    }
  }

  if (!eventEndDate?.type) {
    addValidationIssue({
      path: ['eventEndDate.type'],
      message: 'กรุณาเลือกวันที่ปิดรับสมัคร',
    });
  }
  if (
    eventEndDate?.type === EventEndType.BEFORE_EXAM_DATE &&
    typeof eventEndDate?.date === 'number' &&
    eventEndDate.date < MINIMUM_EXAM_EVENT_CLOSE_BEFORE_EXAM_DATE
  ) {
    addValidationIssue({
      path: ['eventEndDate'],
      message: 'จำนวนวันล่วงหน้าต้องมากกว่า 0 วัน',
    });
  }
  if (
    eventEndDate?.type === EventEndType.BEFORE_EXAM_DATE &&
    (eventEndDate?.date === null || eventEndDate?.date === undefined)
  ) {
    addValidationIssue({
      path: ['eventEndDate'],
      message: 'กรุณาระบุจำนวนวันล่วงหน้าที่ต้องการปิดรับสมัคร',
    });
  }
}

const APPLICATION_GROUP_CODE_MAX_LENGTH = 256;

function validateGroupCode(data: RecurringExamEventCreationFormType) {
  const { groupCode } = data;
  if (!groupCode) {
    return;
  }

  if (groupCode.length > APPLICATION_GROUP_CODE_MAX_LENGTH) {
    addValidationIssue({
      path: ['groupCode'],
      message: `ความยาวต้องไม่เกิน ${APPLICATION_GROUP_CODE_MAX_LENGTH} ตัวอักษร`,
    });
  }

  validateAllowCharacters(groupCode, ['groupCode']);
}

export const RecurringExamEventCreationFormSchema =
  BaseRecurringExamEventCreationFormSchema.superRefine((data, ctx) => {
    superRefineCtx = ctx;
    validateExamEventName(data);
    validateRegistrationType(data);
    validateLocation(data);
    validateExamDate(data);
    validateExamCourses(data);
    validateEventDates(data);
    validateGroupCode(data);
  });
